import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';

import type { FeatureAnnouncement } from '@customer-portal/data-access/feature-announcements';
import { FeatureAnnouncementStoreService } from '@customer-portal/data-access/feature-announcements';

import type { FeatureTourReadinessGateway } from '../../gateways/feature-tour-readiness.gateway';
import { FEATURE_TOUR_READINESS_GATEWAY } from '../../gateways/feature-tour-readiness.gateway';
import {
  FeatureTourControlLabels,
  FeatureTourRun,
  TourOutcome,
} from './feature-tour-run';
import type { FeatureTourRunDependencies } from './feature-tour-run-dependencies.interface';

export type { FeatureTourControlLabels, TourOutcome } from './feature-tour-run';

@Injectable({ providedIn: 'root' })
export class FeatureTourService {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly announcementStore = inject(FeatureAnnouncementStoreService);
  private readonly readinessGateway = inject<FeatureTourReadinessGateway>(
    FEATURE_TOUR_READINESS_GATEWAY,
    { optional: true },
  );
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly ngZone = inject(NgZone);
  private activeTour: FeatureTourRun | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.activeTour?.cancel());
  }

  startTour(
    feature: FeatureAnnouncement,
    controlLabels?: FeatureTourControlLabels,
  ): Observable<TourOutcome> {
    if (!feature.hasTour || !feature.tourSteps?.length) {
      return of('unavailable');
    }

    return new Observable<TourOutcome>((subscriber) => {
      this.activeTour?.cancel();
      const run = new FeatureTourRun(
        feature,
        subscriber,
        this.createRunDependencies(),
        (finishedRun) => {
          if (this.activeTour === finishedRun) {
            this.activeTour = null;
          }
        },
        controlLabels,
      );
      this.activeTour = run;
      run.start();

      return () => run.cancel();
    });
  }

  private createRunDependencies(): FeatureTourRunDependencies {
    return {
      document: this.document,
      announcementStore: this.announcementStore,
      readinessGateway: this.readinessGateway,
      router: this.router,
      ngZone: this.ngZone,
      transloco: this.transloco,
    };
  }
}
